import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Input, Select, Icon, Popover, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ModalBody,
  ModalContainer,
  InputLabel,
  Container,
  InputContainer,
  AddNewCategory,
  PopupContainer,
  PopupActionContainer,
  PopupLabel,
  SaveButton,
  CancelButton,
  WarningLabel,
  InputFlexColumn,
  CategoryInputLabel,
} from './styles';
import { getAllCategories, createCategory } from '../../utils/services/categoriesAPI';
import { AppStoreContext } from '../../context/appStoreContext';
import { getGroupPermission } from '../../utils/services/teamsAPI';
import { CREATE_NEW_BLOCK_MODAL } from './translationValues';
import { createStandaloneBlock } from '../../utils/services/complexDocumentsAPI';
import { DEFAULT_FONT_FOR_LOCALISED_LANGUAGES } from '../../utils/helper';
import { TEMPLATE_NAME_CHAR_LIMIT } from '../../utils/constants/appConstants';
import { AvailableLanguagesDropdown, useSelectedTemplateLanguage } from './AvailableLanguagesDropdown';
import { isLanguageTranslationDisabled } from '../../utils/detectLanguageHelper';
import { useSessionContext } from '../../context/sessionContext';

const { Option } = Select;

const NAMESPACES = ['CREATE_NEW_BLOCK_MODAL',];

const CreateBlockTemplate = props => {
  const { onSucess } = props;
  const { t, i18n } = useTranslation(NAMESPACES);

  const { userDetails } = useContext(AppStoreContext);
  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);
  const { languages, templateLanguage, setTemplateLanguage } = useSelectedTemplateLanguage();
  const userType = userDetails.userType;
  const currentLanguage = i18n.language;

  const [templateName, setTemplateName] = useState('');
  const [validTempName, setValidTempName] = useState(false);
  const [validCategName, setValidCategName] = useState(false);
  const [allCategories, setAllCategories] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState(null);
  const [savePopupVisible, setSavePopupVisible] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [warningText, setWarningText] = useState('');
  const [allTeams, setAllTeams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [savingLoader, setSavingLoader] = useState(false);
  const [blockType, setBlockType] = useState('');

  useEffect(() => {
    (async () => {
      const response = await getAllCategories(true);
      if (response && response.data) {
        let loggedUserCategory = response.data.filter(
          categoryObj => userType === categoryObj.categoryusertype,
        );
        setAllCategories(loggedUserCategory);
      }
      const teamData = await getGroupPermission(true);
      if (teamData?.data) {
        if (teamData.data.length === 1) {
          setSelectedGroup(teamData.data[0].rgId);
        }
        setAllTeams(teamData.data);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const saveNewBlock = async () => {
    const creativeBlock = blockType.toLowerCase() !== 'content' ? [
      {
        "orderNumber": 1,
        "style": {
          "height": 100
        },
        "variableContract": [],
        "contents": [],
        "blockType": "creative"
      }
    ] : [];
    let newBlock = {
      categoryId: selectedCategoryId,
      resourceGroupId: selectedGroup,
      parentBlockName: templateName,
      isReferenced: true,
      blocks: creativeBlock,
      type: blockType.toLowerCase(),
      style: {
        fontFamily: DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
        fontSize: 16,
      }
    };

    if (isLanguageTranslationDisabled(organisationDetails) && templateLanguage) {
      newBlock.language = templateLanguage;
    }

    setSavingLoader(true);
    const response = await createStandaloneBlock(newBlock);
    if (response.statusCode === 2501) {
      onSucess(response.data.id);
    } else {
      if (response && response.errors.length > 0) {
      } else {
        message.error(t(CREATE_NEW_BLOCK_MODAL.SECTION_CREATION_ERROR_MESSAGE.key, CREATE_NEW_BLOCK_MODAL.SECTION_CREATION_ERROR_MESSAGE.value));
      }
    }
    setSavingLoader(false);
  };

  const setCategoryName = categoryName => {
    setNewCategoryName(categoryName.trimStart());
    if (categoryName.split(" ").join("").length) {
      setValidCategName(true);
    }
    else {
      setValidCategName(false)
    }
  };

  const handlePopupVisibleChange = visible => {
    setSavePopupVisible(visible);
  };

  const saveNewCategory = () => {
    setSavingCategory(true);
    let payload = {
      name: newCategoryName,
      type: 'element',
    };
    setSavePopupVisible(false);
    createNewCategory(payload);
  };

  const createNewCategory = async payload => {
    let categoryResponse = await createCategory(payload);
    if (categoryResponse && categoryResponse.errors && categoryResponse.errors.length === 0) {
      const savedCategory = {
        createdBy: null,
        createdOn: null,
        elements: null,
        id: categoryResponse.id,
        name: payload.name,
        parentId: 0,
        status: 'active',
        type: 'element',
        updatedBy: null,
        updatedOn: null,    
      };
      setAllCategories(allCategories => [...allCategories, savedCategory]);
      setSavePopupVisible(false);
      setSelectedCategoryId(categoryResponse.id);
      setNewCategoryName(null);
      setSavingCategory(false);
    } else {
      setSavingCategory(false);
    }
  };

  const changeTempName = name => {
    setTemplateName(name.trimStart())
    if (name.split(" ").join("").length) {
      if(name.trimStart().length > TEMPLATE_NAME_CHAR_LIMIT) {
        setWarningText(t(CREATE_NEW_BLOCK_MODAL.CHAR_LIMIT_EXCEEDED_ERROR.key, CREATE_NEW_BLOCK_MODAL.CHAR_LIMIT_EXCEEDED_ERROR.value, { charLimitLength: TEMPLATE_NAME_CHAR_LIMIT }));
        setValidTempName(true);
        return;  
      }
      setWarningText('');
      setValidTempName(true);
    }
    else {
      setWarningText(t(CREATE_NEW_BLOCK_MODAL.ENTER_VALID_NAME_ERROR_MESSAGE.key, CREATE_NEW_BLOCK_MODAL.ENTER_VALID_NAME_ERROR_MESSAGE.value));
      setValidTempName(false)
    }
  };

  const blockTypeList = [
    { id: 1, name: t(CREATE_NEW_BLOCK_MODAL.HEADER_OPTION.key, CREATE_NEW_BLOCK_MODAL.HEADER_OPTION.value), type: 'header' }, 
    { id: 2, name: t(CREATE_NEW_BLOCK_MODAL.FOOTER_OPTION.key, CREATE_NEW_BLOCK_MODAL.FOOTER_OPTION.value), type: 'footer' }, 
    { id: 3, name: t(CREATE_NEW_BLOCK_MODAL.CONTENT_OPTION.key, CREATE_NEW_BLOCK_MODAL.CONTENT_OPTION.value), type: 'content' }];

  return (
    <Container >
      <ModalBody>
        <ModalContainer >
          <InputContainer>
            <InputLabel>{t(CREATE_NEW_BLOCK_MODAL.BLOCK_NAME_LABEL.key, CREATE_NEW_BLOCK_MODAL.BLOCK_NAME_LABEL.value)}</InputLabel>
            <Input
              placeholder={t(CREATE_NEW_BLOCK_MODAL.BLOCK_NAME_PLACEHOLDER.key, CREATE_NEW_BLOCK_MODAL.BLOCK_NAME_PLACEHOLDER.value)}
              autoComplete="off"
              name="templateName"
              onChange={e => { changeTempName(e.target.value) }}
              size="large"
              value={templateName}
            />
            {warningText && <WarningLabel>{warningText}</WarningLabel>}
          </InputContainer>

          <InputContainer>
            <InputFlexColumn>
                <AddNewCategory>
                  <CategoryInputLabel>{t(CREATE_NEW_BLOCK_MODAL.SELECT_CATEGORY_LABEL.key, CREATE_NEW_BLOCK_MODAL.SELECT_CATEGORY_LABEL.value)}</CategoryInputLabel>
                  <Popover
                    content={
                      <PopupContainer>
                        <PopupLabel>{t(CREATE_NEW_BLOCK_MODAL.CATEGORY_NAME_LABEL.key, CREATE_NEW_BLOCK_MODAL.CATEGORY_NAME_LABEL.value)}</PopupLabel>
                        <Input
                          placeholder={t(CREATE_NEW_BLOCK_MODAL.CATEGORY_NAME_PLACEHOLDER.key, CREATE_NEW_BLOCK_MODAL.CATEGORY_NAME_PLACEHOLDER.value)}
                          autoComplete="off"
                          name="categoryName"
                          value={newCategoryName}
                          onChange={e => setCategoryName(e.target.value)}
                        />
                        <PopupActionContainer>
                          <CancelButton
                            size="default"
                            onClick={() => handlePopupVisibleChange(false)}
                          >
                            {t(CREATE_NEW_BLOCK_MODAL.CANCEL_BUTTON.key, CREATE_NEW_BLOCK_MODAL.CANCEL_BUTTON.value)}
                          </CancelButton>
                          <SaveButton
                            type="primary"
                            size="default"
                            onClick={() => saveNewCategory()}
                            disabled={!newCategoryName || !validCategName}
                            loading={savingCategory}
                          >
                            {savingCategory ? t(CREATE_NEW_BLOCK_MODAL.SAVING_CATEGORY_LOADING_TEXT.key, CREATE_NEW_BLOCK_MODAL.SAVING_CATEGORY_LOADING_TEXT.value) : t(CREATE_NEW_BLOCK_MODAL.SAVE_BUTTON.key, CREATE_NEW_BLOCK_MODAL.SAVE_BUTTON.value)}
                          </SaveButton>
                        </PopupActionContainer>
                      </PopupContainer>
                    }
                    placement="leftBottom"
                    trigger="click"
                    visible={savePopupVisible}
                    onVisibleChange={handlePopupVisibleChange}
                  >
                    <Icon type="plus" /> {t(CREATE_NEW_BLOCK_MODAL.ADD_NEW_ACTION.key, CREATE_NEW_BLOCK_MODAL.ADD_NEW_ACTION.value)}
                  </Popover>
                </AddNewCategory>
                <Select
                  loading={!allCategories}
                  showSearch
                  placeholder={t(CREATE_NEW_BLOCK_MODAL.SELECT_CATEGORY_PLACEHOLDER.key, CREATE_NEW_BLOCK_MODAL.SELECT_CATEGORY_PLACEHOLDER.value)}
                  optionFilterProp="children"
                  value={selectedCategoryId ? selectedCategoryId : undefined}
                  onChange={value => setSelectedCategoryId(value)}
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {allCategories &&
                    allCategories.map(data => {
                      return (
                        <Option key={String(data.id)} value={data.id}>
                          {data.name}
                        </Option>
                      );
                    })}
                </Select>
            </InputFlexColumn>
          </InputContainer>

          {allTeams.length > 1 && (
            <InputContainer>
              <InputLabel>{t(CREATE_NEW_BLOCK_MODAL.SELECT_TEAM_LABEL.key, CREATE_NEW_BLOCK_MODAL.SELECT_TEAM_LABEL.value)}</InputLabel>
              <Select
                loading={!allTeams}
                showSearch
                placeholder={t(CREATE_NEW_BLOCK_MODAL.SELECT_TEAM_PLACEHOLDER.key, CREATE_NEW_BLOCK_MODAL.SELECT_TEAM_PLACEHOLDER.value)}
                optionFilterProp="children"
                value={selectedGroup || undefined}
                onChange={value => setSelectedGroup(value)}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allTeams &&
                  allTeams.map(data => {
                    return (
                      <Option key={String(data.id)} value={data.rgId}>
                        {data.groupName}
                      </Option>
                    );
                  })}
              </Select>
            </InputContainer>
          )}
          <InputContainer>
            <InputLabel>{t(CREATE_NEW_BLOCK_MODAL.SELECT_BLOCK_TYPE_LABEL.key, CREATE_NEW_BLOCK_MODAL.SELECT_BLOCK_TYPE_LABEL.value)}</InputLabel>
            <Select
              showSearch
              placeholder={t(CREATE_NEW_BLOCK_MODAL.SELECT_BLOCK_TYPE_LABEL.key, CREATE_NEW_BLOCK_MODAL.SELECT_BLOCK_TYPE_LABEL.value)}
              optionFilterProp="children"
              value={blockType || undefined}
              onChange={value => setBlockType(value)}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {
                blockTypeList.map(data => {
                  return (
                    <Option key={String(data.id)} value={data.type}>
                      {data.name}
                    </Option>
                  );
                })}
            </Select>
          </InputContainer>
          <AvailableLanguagesDropdown
            languages={languages}
            templateLanguage={templateLanguage}
            setTemplateLanguage={setTemplateLanguage}
          />
          <InputContainer>
            <div className="form_btn">
              <Button
                disabled={!templateName || !validTempName || !selectedCategoryId || (allTeams.length > 1 && !selectedGroup) || !blockType || templateName.trimStart().length > TEMPLATE_NAME_CHAR_LIMIT}
                key="submit"
                type="primary"
                onClick={saveNewBlock}
                loading={savingLoader}
              >
                {t(CREATE_NEW_BLOCK_MODAL.START_DESIGN_BUTTON.key, CREATE_NEW_BLOCK_MODAL.START_DESIGN_BUTTON.value)}
              </Button>
            </div>
          </InputContainer>
        </ModalContainer>
      </ModalBody>
    </Container>
  );
};
export default CreateBlockTemplate;
